import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPayments } from "../customerSchemeApi";
import "../CustomerScheme.css";

const getMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.message || fallback;

// Build a lookup map from enrollment cache: enrollmentId → { schemeName, productCode }
const buildEnrollmentMap = (authData) => {
  const identityId = authData?.customerMasterId || authData?.customerId;
  const cacheKey = identityId
    ? `customer_scheme_enrollments_cache_${identityId}`
    : "customer_scheme_enrollment_cache";
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey) || "[]");
    const list = Array.isArray(cached) ? cached : [cached];
    const map = {};
    list.forEach((enr) => {
      if (!enr) return;
      const id = String(enr?._id || enr?.id || "");
      if (id) {
        map[id] = {
          schemeName: enr?.schemeName || "-",
          productCode: enr?.productCode || enr?.comboCode || "-",
        };
      }
    });
    return map;
  } catch {
    return {};
  }
};

// Resolve a human-readable scheme label from payment record + enrollment map
const resolveSchemeLabel = (payment, enrollmentMap) => {
  // Try direct fields on payment first
  const direct =
    payment?.schemeName ||
    payment?.productName ||
    payment?.comboName ||
    payment?.productCode;
  if (direct) return String(direct);

  // Try enriching via enrollmentId → enrollment cache lookup
  const eid = String(payment?.enrollmentId?.$oid || payment?.enrollmentId || "");
  if (eid && enrollmentMap[eid]) {
    const { schemeName, productCode } = enrollmentMap[eid];
    return `${schemeName} - ${productCode}`;
  }

  return null; // unknown
};

const normalizeFrequencyLabel = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (!normalized) {
    return "-";
  }
  return normalized.charAt(0) + normalized.slice(1).toLowerCase();
};

const getModeDisplayLabel = (mode) => {
  if (mode === "ALL") {
    return "All Payments";
  }
  if (mode === "PAYMENT_PLAN") {
    return "Scheduled Payments";
  }
  if (mode === "LEDGER") {
    return "Flexible Payments";
  }
  return mode;
};

const PaymentHistory = () => {
  const navigate = useNavigate();
  const authData = JSON.parse(sessionStorage.getItem("customer_scheme_auth") || "{}");
  const customerMasterId = authData?.customerMasterId;
  const customerId = authData?.customerId;
  const customerPhone = authData?.phone || authData?.mobile;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rawPayments, setRawPayments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [schemeFilter, setSchemeFilter] = useState("ALL");
  const [modeFilter, setModeFilter] = useState("ALL");

  // Enrollment cache map, built once on mount
  const enrollmentMap = useMemo(() => buildEnrollmentMap(authData), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    sessionStorage.removeItem("customer_scheme_auth");
    localStorage.removeItem("customer_scheme_auth");
    navigate("/customer/login");
  };

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        const queryParams = customerMasterId
          ? { customerMasterId }
          : customerId
            ? { customerId }
            : {};
        const response = await fetchPayments(queryParams);
        const list = response?.data?.data || response?.data || [];
        setRawPayments(Array.isArray(list) ? list : []);
      } catch (err) {
        setError(getMessage(err, "Unable to fetch payment history."));
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [customerMasterId, customerId, customerPhone]);

  // Customer-scoped payments enriched with scheme label
  const enrichedPayments = useMemo(() => {
    const enrollmentIds = new Set(Object.keys(enrollmentMap || {}));
    return rawPayments
      .filter((payment) => {
        const paymentEnrollmentId = String(payment?.enrollmentId?.$oid || payment?.enrollmentId || "");
        if (paymentEnrollmentId && enrollmentIds.has(paymentEnrollmentId)) {
          return true;
        }
        if (customerMasterId && String(payment?.customerMasterId) === String(customerMasterId)) return true;
        if (customerId && String(payment?.customerId) === String(customerId)) return true;
        if (customerPhone && String(payment?.customerPhone || payment?.phone) === String(customerPhone)) return true;
        return !customerMasterId && !customerId && !customerPhone;
      })
      .map((payment) => ({
        ...payment,
        _schemeLabel: resolveSchemeLabel(payment, enrollmentMap) || "Other",
      }));
  }, [rawPayments, customerMasterId, customerId, customerPhone, enrollmentMap]);

  // Distinct scheme names for the filter dropdown (using readable label, never ids)
  const schemeOptions = useMemo(() => {
    const unique = new Set(enrichedPayments.map((p) => p._schemeLabel));
    return ["ALL", ...Array.from(unique)];
  }, [enrichedPayments]);

  // Final filtered list
  const payments = useMemo(() => {
    const filtered = enrichedPayments.filter((payment) => {
      const statusMatch =
        statusFilter === "ALL" ||
        String(payment?.status || "").toUpperCase() === statusFilter;
      const schemeMatch =
        schemeFilter === "ALL" || payment._schemeLabel === schemeFilter;
      const modeMatch =
        modeFilter === "ALL" || String(payment?.modeUsed || "").toUpperCase() === modeFilter;
      return statusMatch && schemeMatch && modeMatch;
    });
    return filtered;
  }, [enrichedPayments, statusFilter, schemeFilter, modeFilter]);

  const getStatusClass = (status) => {
    const value = (status || "pending").toLowerCase();
    if (value === "approved" || value === "paid" || value === "success") {
      return "customer-status customer-status-paid";
    }
    if (value === "rejected") {
      return "customer-status customer-status-rejected";
    }
    return "customer-status customer-status-pending";
  };

  const getStatusLabel = (status) => {
    const value = (status || "pending").toLowerCase();
    if (value === "success") {
      return "Approved";
    }
    if (value === "pending") {
      return "Waiting for Approval";
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  return (
    <div className="customer-scheme-shell">
      <div className="customer-scheme-container">
        <div className="customer-header">
          <div>
            <h2>Payment History</h2>
            <p className="customer-subtitle">Track each submitted payment and approval status.</p>
          </div>
          <div className="customer-row">
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/dashboard")}>Back to Dashboard</button>
            <button className="customer-btn customer-btn-secondary" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        <div className="customer-card customer-table-wrap">
          {loading ? (
            <div>Loading history...</div>
          ) : error ? (
            <div className="customer-error">{error}</div>
          ) : (
            <>
              <div className="customer-history-filters">
                <div className="customer-field">
                  <label>Status Filter</label>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="ALL">All</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="PAID">Paid</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="SUCCESS">Success</option>
                  </select>
                </div>

                <div className="customer-field">
                  <label>Mode Filter</label>
                  <div className="customer-row" style={{ flexWrap: "wrap" }}>
                    {["ALL", "PAYMENT_PLAN", "LEDGER"].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        className={`customer-btn ${modeFilter === mode ? "customer-btn-primary" : "customer-btn-secondary"}`}
                        onClick={() => setModeFilter(mode)}
                      >
                        {getModeDisplayLabel(mode)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="customer-field">
                  <label>Scheme Filter</label>
                  <select value={schemeFilter} onChange={(e) => setSchemeFilter(e.target.value)}>
                    {schemeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option === "ALL" ? "All Schemes" : option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <table className="customer-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Mode</th>
                    <th>Plan</th>
                    <th>Amount Paid</th>
                    <th>Expected Amount</th>
                    <th>Status</th>
                    <th>Reference Number</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan="7">No payments found.</td>
                    </tr>
                  ) : (
                    payments.map((payment, index) => (
                      <tr key={payment?._id || payment?.id || index}>
                        <td>
                          {payment?.date
                            ? new Date(payment.date).toLocaleDateString()
                            : payment?.paymentDate
                              ? new Date(payment.paymentDate).toLocaleDateString()
                              : payment?.createdAt
                                ? new Date(payment.createdAt).toLocaleDateString()
                                : "-"}
                        </td>
                        <td>{payment?.modeUsed || "-"}</td>
                        <td>{normalizeFrequencyLabel(payment?.frequencyAtPaymentTime || payment?.paymentPlanSnapshot?.selectedPaymentFrequency)}</td>
                        <td>Rs. {payment?.paidAmount || payment?.amount || 0}</td>
                        <td>Rs. {payment?.expectedAmount || payment?.paymentPlanSnapshot?.selectedPaymentAmount || 0}</td>
                        <td><span className={getStatusClass(payment?.status)}>{getStatusLabel(payment?.status)}</span></td>
                        <td>{payment?.referenceNumber || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
